import React from 'react';
import { Box, IconButton, MenuItem, Select, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DataCell, DataRow } from '../Tables/DataList';
import { countSummary, pageWindow, PER_PAGE_OPTIONS, totalPages } from './paginationUtils';

export { DEFAULT_PER_PAGE } from './paginationUtils';

/**
 * Skeleton placeholder rows shown while a tab's page is loading.
 * `columns` is the list of `xs` spans (matching the tab's real columns).
 */
export function TabSkeletonRows(props: { rows: number; columns: number[] }) {
  const { rows, columns } = props;
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <DataRow key={`skeleton-${rowIndex}`}>
          {columns.map((xs, columnIndex) => (
            <DataCell key={columnIndex} xs={xs}>
              <Skeleton variant='text' />
            </DataCell>
          ))}
        </DataRow>
      ))}
    </>
  );
}

export function TabEmpty(props: { label?: string }) {
  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <Typography variant='body2' color='text.secondary'>{props.label ?? 'No records'}</Typography>
    </Box>
  );
}

const PaginationFooter = styled('div', { shouldForwardProp: (prop) => prop !== 'inline' })<{ inline?: boolean }>(
  ({ inline }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    ...(inline
      ? { borderTop: '1px solid rgba(0, 0, 0, 0.08)', paddingTop: 12, marginTop: 8 }
      : { flexShrink: 0, height: 56, borderTop: '1px solid rgba(0, 0, 0, 0.1)', background: '#fff', paddingLeft: 16, paddingRight: 16, marginLeft: 24, marginRight: 24 }),
  }),
);

const PagerLeft = styled('div')({ display: 'flex', alignItems: 'center', gap: 16 });
const PerPageLabel = styled('span')({ fontSize: 13, color: '#5a6472' });
const CountLabel = styled('span')({ fontSize: 14, color: '#5a6472' });
const PerPageSelect = styled(Select)({ height: 34, fontSize: 14, backgroundColor: '#fff' });
const PagerNav = styled('div')({ display: 'flex', alignItems: 'center', gap: 4, position: 'absolute', left: '50%', transform: 'translateX(-50%)' });

const NavButton = styled(IconButton)({ color: '#37424f', width: 32, height: 32, '&.Mui-disabled': { opacity: 0.35 } });

const PageButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
  ({ active }) => ({
    minWidth: 32,
    height: 32,
    borderRadius: '50%',
    fontSize: 14,
    ...(active ? { background: '#1976d2', color: '#fff', '&:hover': { background: '#135ea7' } } : { color: '#37424f' }),
  }),
);

/**
 * Bottom-pinned (or inline) pagination bar for a company-detail section: a
 * per-page selector (10/15/25/50, default 10), a result-count summary, and
 * first / previous / numbered / next / last controls. Always renders — a
 * single-page section shows the count with the nav controls inactive.
 */
export function PaginationBar(props: {
  count: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  onPageSize: (pageSize: number) => void;
  variant?: 'pinned' | 'inline';
  perPageOptions?: number[];
}) {
  const { count, page, pageSize, onPage, onPageSize, variant = 'pinned', perPageOptions } = props;
  // Keep the current pageSize selectable even if a caller passes options that omit it.
  const options = (perPageOptions ?? PER_PAGE_OPTIONS).includes(pageSize)
    ? (perPageOptions ?? PER_PAGE_OPTIONS)
    : [...(perPageOptions ?? PER_PAGE_OPTIONS), pageSize].sort((a, b) => a - b);
  const pages = totalPages(count, pageSize);
  const atFirst = page <= 1;
  const atLast = page >= pages;

  return (
    <PaginationFooter inline={variant === 'inline'}>
      <PagerLeft>
        <PerPageLabel>Per Page</PerPageLabel>
        <PerPageSelect size='small' value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))}>
          {options.map((opt) => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
        </PerPageSelect>
        <CountLabel>{countSummary(page, pageSize, count)}</CountLabel>
      </PagerLeft>
      <PagerNav>
        <NavButton aria-label='First page' size='small' disabled={atFirst} onClick={() => onPage(1)}><FirstPageIcon /></NavButton>
        <NavButton aria-label='Previous page' size='small' disabled={atFirst} onClick={() => onPage(page - 1)}><ChevronLeftIcon /></NavButton>
        {pageWindow(page, pages).map((n) => (
          <PageButton key={n} active={n === page} aria-label={`Page ${n}`} aria-current={n === page ? 'page' : undefined} size='small' onClick={() => onPage(n)}>{n}</PageButton>
        ))}
        <NavButton aria-label='Next page' size='small' disabled={atLast} onClick={() => onPage(page + 1)}><ChevronRightIcon /></NavButton>
        <NavButton aria-label='Last page' size='small' disabled={atLast} onClick={() => onPage(pages)}><LastPageIcon /></NavButton>
      </PagerNav>
    </PaginationFooter>
  );
}

// Shared quiet pill for the tab controls: soft fill, hairline on hover, primary ring only while focused.
const quietPill = {
  backgroundColor: '#F5F7FA',
  borderRadius: 8,
  height: 38,
  fontSize: '0.875rem',
  transition: 'background-color 0.15s, box-shadow 0.15s',
  '& fieldset': { border: '1px solid transparent' },
  '&:hover': { backgroundColor: '#F0F3F8' },
  '&:hover fieldset': { border: '1px solid rgba(0,0,0,0.12)' },
  '&.Mui-focused': { backgroundColor: '#fff', boxShadow: '0 0 0 3px rgba(25,118,210,0.12)' },
  '&.Mui-focused fieldset': { border: '1px solid #1976d2' },
};

const SearchFieldRoot = styled(TextField)({
  width: 560,
  maxWidth: '100%',
  '& .MuiOutlinedInput-root': quietPill,
  '& .MuiOutlinedInput-input::placeholder': { color: '#8a94a3', opacity: 1 },
});

// Active filters take the workspace's tinted treatment so a filtered table reads as filtered.
const FILTER_ACTIVE_BG = '#eef4fc';
const FILTER_ACTIVE_BORDER = 'rgba(25,118,210,0.25)';

const SelectFilterRoot = styled(Select, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
  ({ active }) => ({
    ...quietPill,
    minWidth: 150,
    ...(active && {
      backgroundColor: FILTER_ACTIVE_BG,
      '&:hover': { backgroundColor: '#e4eefb' },
      '&.Mui-focused': { backgroundColor: FILTER_ACTIVE_BG, boxShadow: '0 0 0 3px rgba(25,118,210,0.12)' },
      '& fieldset': { border: `1px solid ${FILTER_ACTIVE_BORDER}` },
      '&:hover fieldset': { border: '1px solid rgba(25,118,210,0.45)' },
    }),
    '& .MuiSelect-select': {
      paddingTop: 0,
      paddingBottom: 0,
      display: 'flex',
      alignItems: 'center',
      height: '38px !important',
      ...(active && { paddingRight: '58px !important' }),
    },
  }),
);

const FilterValue = styled('span')({ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 });

const FilterValueIcon = styled(FilterListIcon)({ fontSize: 16, color: '#1976d2', flexShrink: 0 });

const FilterValueLabel = styled('span', { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
  ({ active }) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...(active ? { fontWeight: 600, color: '#1565c0' } : { color: '#5a6472' }),
  }),
);

// Absolutely positioned so it clears the select's caret without reflowing the label.
const ClearFilterButton = styled(IconButton)({
  position: 'absolute',
  right: 32,
  padding: 2,
  color: '#5a6472',
  '&:hover': { backgroundColor: 'rgba(25,118,210,0.12)' },
});

export interface TabSelectFilterOption {
  value: string;
  label: string;
}

/** Controlled single-select filter for the v2 tab tables; the empty value renders `emptyLabel`. */
export function TabSelectFilter(props: {
  value: string;
  onChange: (value: string) => void;
  options: TabSelectFilterOption[];
  emptyLabel: string;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const { value, onChange, options, emptyLabel, ariaLabel, disabled } = props;
  return (
    <SelectFilterRoot
      size='small'
      displayEmpty
      active={!!value}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value as string)}
      renderValue={(selected) => (
        <FilterValue>
          {!!selected && <FilterValueIcon />}
          <FilterValueLabel active={!!selected}>
            {selected ? options.find((o) => o.value === selected)?.label ?? String(selected) : emptyLabel}
          </FilterValueLabel>
        </FilterValue>
      )}
      endAdornment={value ? (
        <ClearFilterButton
          size='small'
          aria-label={`Clear ${ariaLabel.toLowerCase()}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onChange(''); }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </ClearFilterButton>
      ) : undefined}
      inputProps={{ 'aria-label': ariaLabel }}
    >
      <MenuItem value=''>{emptyLabel}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
      ))}
    </SelectFilterRoot>
  );
}

/** Controlled search input for the v2 tab tables: icon, placeholder, and a clear button while a term is set. */
export function TabSearchField(props: { value: string; onChange: (value: string) => void; placeholder: string; sx?: object }) {
  const { value, onChange, placeholder, sx } = props;
  return (
    <SearchFieldRoot
      size='small'
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputProps={{ 'aria-label': placeholder }}
      sx={sx}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon fontSize='small' sx={{ color: '#8a94a3' }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position='end'>
            <IconButton size='small' aria-label='Clear search' onClick={() => onChange('')}>
              <CloseIcon sx={{ fontSize: 16, color: '#8a94a3' }} />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}
